// Everything an operator wants to SAY, in one optional block. Lifted from the
// Andon app's `SayItBlock`, narrowed to what this step of the merge carries.
//
// The fixed answers above it are what route the alert — they resolve to
// records, and a record resolves to a manager. This is the other half: the
// sentence, the photo of the empty rack, the words said instead of typed.
// Any combination, none of them required.
//
// WHY IT IS NOT A MODAL
//
// It used to be. The photo lived behind a button on the live screen, after the
// raise, and the voice note was a hold-to-talk card on the severity screen two
// steps earlier — so the two things a gloved operator can actually do quickly
// were on different screens, neither of them where they were thinking about
// what was wrong. They stack here as removable rows: nothing is a detour, and
// nothing is lost by adding one more.
//
// EVERY ROW BECOMES ONE MESSAGE
//
// The moment the alert exists, all of this — the answers, the words, and every
// photo — is posted into the thread as ONE bubble from the raiser. It used to
// be one message per file, so a raise with three photos and a sentence read as
// four separate things nobody had said together.
//
// WHAT CAME ACROSS, AND WHAT DID NOT. Words, dictation into the box, and
// photos — the three the frame names for this step. The Andon app also
// offered an audio clip, a video clip and a file upload here; those ride on a
// recorder hook and a viewfinder the shop floor does not have yet, and they
// belong with the thread's composer in the step that brings it. The draft
// already holds every kind (see `DraftAttachment`), so adding them is rows,
// not plumbing. No GIF, and not an oversight to fix later: a GIF is a reply to
// a person, and at the moment of raising there is nobody in the conversation.
//
// Dictation is the shop floor's own: the browser's speech service, in the
// app's language, behind the consent the quality alert form already asks for
// once and remembers. The transcript lands in the box as a draft to correct,
// never straight on the record.

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { useDictation } from '@/hooks/useDictation';
import { useT } from '@/i18n/useT';
import { downscaleToJpeg } from '@/lib/image';
import { useStore } from '@/store';
import type { DraftAttachment } from '@/store/slices/andon';

/** What the server accepts for the words. Longer belongs in the thread. */
const MAX_WORDS = 2000;

export function SayItBlock() {
  const t = useT();
  const words = useStore((s) => s.draftWords);
  const setWords = useStore((s) => s.setDraftWords);
  const attachments = useStore((s) => s.draftAttachments);
  const add = useStore((s) => s.addDraftAttachment);
  const remove = useStore((s) => s.removeDraftAttachment);
  const showError = useStore((s) => s.showError);
  const language = useStore((s) => s.language);
  const consent = useStore((s) => s.speechConsent);
  const setConsent = useStore((s) => s.setSpeechConsent);
  const type = useStore((s) => s.draftType);

  /**
   * The heading, which is the tile's only say in this block.
   *
   * "Anything else worth sending?" is right on four tiles, where the chips
   * above have already carried the routing and this is a genuine extra. On
   * machine down it is wrong: the photo is not an extra, it is half the
   * answer, and a heading that calls it "anything else" tells the operator it
   * is the part nobody reads.
   */
  const heading = type === 'equipment' ? t.sayShowItT : t.sayAnythingElseT;

  const photoInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const dictation = useDictation(language);

  const onPhoto = async (file: File) => {
    setBusy(true);
    try {
      // Downscaled before it ever enters state. A raw phone JPEG held as a
      // base64 string is enough to kill the WebView on the Zebra tablets.
      const data = await downscaleToJpeg(file);
      add({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        kind: 'photo',
        name: file.name || 'photo.jpg',
        data,
        size: Math.round((data.length * 3) / 4),
        seconds: null,
      });
    } catch {
      showError(t.sayPhotoFailedT);
    } finally {
      setBusy(false);
    }
  };

  // Dictation types into the same box rather than producing a second artefact:
  // somebody who cannot type with gloves on wants words in the field, and the
  // field is where they can fix what the recogniser misheard.
  const toggleDictation = () => {
    if (dictation.listening) {
      dictation.stop();
      const heard = dictation.transcript.trim();
      if (heard) setWords(((words ? `${words} ` : '') + heard).slice(0, MAX_WORDS));
    } else {
      dictation.start();
    }
  };

  const hasPhoto = attachments.some((a) => a.kind === 'photo');

  return (
    <section
      style={{
        marginTop: 12,
        padding: '15px 16px 14px',
        borderRadius: 18,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 11 }}>
        <span style={{ font: '700 13px Manrope,sans-serif', color: 'var(--ink)' }}>{heading}</span>
        <span style={{ font: '600 10.5px Manrope,sans-serif', color: 'var(--sub)' }}>{t.optionalT}</span>
      </div>
      {/*
        THE ONE TILE THAT ASKS FOR A PHOTO BY NAME.

        Everything else on this card is deliberately generic — the block is
        optional, and an app that nags for evidence on every raise gets its
        nagging ignored on the one where it matters. Machine down is that one.
        The objective on this tile is the machine, its error codes and what was
        already tried, and two of those three are on a display and a control
        panel that a photo captures completely and a description does not.

        Still a prompt and never a gate. The send is live either way — the
        questions above are what route the alert, and holding a stopped machine
        hostage to a camera would be the worst version of this.
      */}
      {type === 'equipment' && !hasPhoto && (
        <p style={{ margin: '0 0 11px', font: '500 11px/1.55 Manrope,sans-serif', color: 'var(--sub)' }}>{t.sayEquipmentPhotoT}</p>
      )}

      <textarea
        value={words ?? ''}
        onChange={(e) => setWords(e.target.value.slice(0, MAX_WORDS) || null)}
        rows={2}
        placeholder={t.sayPlaceholderT}
        aria-label={heading}
        style={{
          width: '100%',
          padding: '11px 13px',
          borderRadius: 13,
          background: 'var(--bg)',
          border: '1px solid var(--line)',
          color: 'var(--ink)',
          font: '500 12.5px/1.55 Manrope,sans-serif',
          resize: 'none',
          outline: 'none',
        }}
      />

      {dictation.supported && (
        <div style={{ marginTop: 8 }}>
          {/*
            The browser's speech recognition sends audio off the device on
            most platforms. Consent is asked for once and remembered — never
            assumed — and it is the same consent the quality form asks for, so
            a device that said yes there has said yes here.
          */}
          {!consent ? (
            <button
              type="button"
              className="press"
              onClick={() => setConsent(true)}
              style={{
                width: '100%',
                minHeight: 44,
                borderRadius: 12,
                background: 'var(--surface2)',
                border: '1.5px solid var(--line)',
                font: '500 11.5px Manrope,sans-serif',
                color: 'var(--sub)',
                padding: '8px 12px',
                textAlign: 'left',
              }}
            >
              {t.voiceNote} — {t.voiceConsentAsk}
            </button>
          ) : (
            <>
              <Button
                variant={dictation.listening ? 'danger' : 'surface'}
                size="md"
                onClick={toggleDictation}
                icon={<Icon name={dictation.listening ? 'hazard' : 'mic'} size={16} />}
              >
                {dictation.listening ? t.stopT : t.voiceNote}
              </Button>
              {dictation.listening && (
                <p
                  aria-live="polite"
                  style={{
                    font: '500 11.5px Manrope,sans-serif',
                    color: 'var(--sub)',
                    background: 'var(--surface2)',
                    borderRadius: 10,
                    padding: 10,
                    margin: '8px 0 0',
                    minHeight: 20,
                  }}
                >
                  {dictation.transcript || `${t.voiceNote}…`}
                </p>
              )}
              {dictation.error && (
                <p role="status" style={{ font: '500 11px Manrope,sans-serif', color: 'var(--danger)', margin: '8px 0 0' }}>
                  {dictation.error}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 9 }}>
          {attachments.map((a) => (
            <AttachmentRow key={a.id} attachment={a} onRemove={() => remove(a.id)} />
          ))}
        </div>
      )}

      {/* The same dashed control the quality form uses for its photo, so one
          gesture takes a picture on either raise. Several are allowed here —
          a stopped machine is a display AND a panel. */}
      <button
        type="button"
        className="press"
        disabled={busy}
        onClick={() => photoInput.current?.click()}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          height: 46,
          borderRadius: 12,
          background: 'var(--surface2)',
          border: '1.5px dashed var(--line)',
          font: '600 12.5px Manrope,sans-serif',
          color: 'var(--ink)',
          marginTop: 9,
          opacity: busy ? 0.55 : 1,
        }}
      >
        <Icon name="camera" size={17} />
        {hasPhoto ? t.sayAnotherPhotoT : t.takePhoto}
      </button>
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onPhoto(file);
          e.target.value = '';
        }}
      />
    </section>
  );
}

/** 1.2 MB, 940 kB, 14 kB. What a person recognises as "is this too big". */
function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}

/** One stacked row. Removable, because adding one must not be a commitment. */
function AttachmentRow({ attachment, onRemove }: { attachment: DraftAttachment; onRemove: () => void }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 9px 7px 7px', borderRadius: 12, background: 'var(--surface2)' }}>
      {/* The picture itself, small: the one check worth making before a send
          is that the camera got the panel and not the floor. */}
      {attachment.kind === 'photo' ? (
        <img
          src={`data:image/jpeg;base64,${attachment.data}`}
          alt=""
          style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', flex: 'none' }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{ width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', flex: 'none', background: 'var(--surface)', color: 'var(--sub)' }}
        >
          <Icon name="file" size={17} />
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            font: '600 11.5px Manrope,sans-serif',
            color: 'var(--ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {attachment.name}
        </span>
        <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
          {t.justNowT} · {fileSize(attachment.size)}
        </span>
      </span>
      <button
        type="button"
        className="press"
        aria-label={`${t.removeT} ${attachment.name}`}
        onClick={onRemove}
        style={{ width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center', color: 'var(--sub)', flex: 'none' }}
      >
        <Icon name="cross" size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
